import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Snackbar } from '@evaneos/b2b-ui';
import CheckIcon from '@evaneos/b2b-ui/icons/Check';
import SyncIcon from '@evaneos/b2b-ui/icons/Sync';

import { useWebSocket } from '../../../../shared/providers/WebSocketProvider';
import { useAuth } from '../../../../shared/providers/AuthProvider';
import { queryClient } from '../../../../shared/providers/react-query-provider';
import { getPhoneCallsQueryKey } from '../../../../resources/phoning/queries';
import {
    PHONE_CALL_TRANSCRIPT_WEBSOCKET_ACTIONS,
    PhoneCallTranscriptWebSocketEvent,
    TranscriptEventType,
} from '../../../../resources/phoning/types';
import { useTranscriptGeneration } from '../../../../shared/providers/TranscriptGenerationProvider';

import { ProcessingStartedNotification } from './ProcessingStartedNotification';
import { ProcessingCompletedNotification } from './ProcessingCompletedNotification';
import { ProcessingFailedNotification } from './ProcessingFailedNotification';
import { mapTranscriptEvent } from './mapTranscriptEvent';
import type { TranscriptNotificationState, TranscriptEventAttributes } from './types';

export const TranscriptStatusNotification = () => {
    const { socket } = useWebSocket();
    const { userId: currentUserId } = useAuth();
    const { startTranscriptGeneration, stopTranscriptGeneration, stopSummaryGeneration } =
        useTranscriptGeneration();
    const [notificationState, setNotificationState] = useState<TranscriptNotificationState>({
        type: null,
        dossierId: null,
        phoneCallId: null,
        dossierPhoneCallId: null,
        isVisible: false,
        transcriptErrorCode: null,
        summaryErrorCode: null,
        initiatingAction: null,
    });

    const stateRef = useRef(notificationState);
    useEffect(() => {
        stateRef.current = notificationState;
    }, [notificationState]);

    const invalidatePhoneCalls = useCallback((dossierId: number) => {
        return queryClient.invalidateQueries({
            queryKey: getPhoneCallsQueryKey(dossierId.toString()),
        });
    }, []);

    const handleProcessStarted = useCallback(
        (
            attrs: TranscriptEventAttributes,
            dossierPhoneCallId: string,
            eventMetadata: { isSummaryOnly: boolean; eventType: TranscriptEventType },
        ) => {
            if (!eventMetadata.isSummaryOnly) {
                startTranscriptGeneration(attrs.phoneCallId);
            }

            setNotificationState((prev) => ({
                ...prev,
                type: eventMetadata.eventType,
                dossierId: attrs.dossierId.toString(),
                phoneCallId: attrs.phoneCallId,
                dossierPhoneCallId,
                isVisible: true,
                transcriptErrorCode: attrs.transcriptErrorCode,
                summaryErrorCode: attrs.summaryErrorCode,
                initiatingAction: attrs.action,
            }));
        },
        [startTranscriptGeneration],
    );

    const handleProcessFailed = useCallback(
        (attrs: TranscriptEventAttributes, dossierPhoneCallId: string) => {
            if (attrs.transcriptErrorCode) {
                stopTranscriptGeneration(attrs.phoneCallId);
            }
            if (attrs.summaryErrorCode) {
                stopSummaryGeneration(attrs.phoneCallId);
            }

            setNotificationState((prev) => ({
                ...prev,
                type: 'call-transcription-failed',
                dossierId: attrs.dossierId.toString(),
                phoneCallId: attrs.phoneCallId,
                dossierPhoneCallId,
                isVisible: true,
                transcriptErrorCode: attrs.transcriptErrorCode,
                summaryErrorCode: attrs.summaryErrorCode,
                initiatingAction: attrs.action,
            }));

            void invalidatePhoneCalls(attrs.dossierId);
        },
        [stopTranscriptGeneration, stopSummaryGeneration, invalidatePhoneCalls],
    );

    const handleProcessCompleted = useCallback(
        (
            attrs: TranscriptEventAttributes,
            dossierPhoneCallId: string,
            eventMetadata: { isSummaryOnly: boolean; eventType: TranscriptEventType },
        ) => {
            if (eventMetadata.isSummaryOnly) {
                stopSummaryGeneration(attrs.phoneCallId);
            } else {
                stopTranscriptGeneration(attrs.phoneCallId);
                stopSummaryGeneration(attrs.phoneCallId);
            }

            setNotificationState((prev) => ({
                ...prev,
                type: eventMetadata.eventType,
                dossierId: attrs.dossierId.toString(),
                phoneCallId: attrs.phoneCallId,
                dossierPhoneCallId,
                isVisible: true,
                transcriptErrorCode: attrs.transcriptErrorCode,
                summaryErrorCode: attrs.summaryErrorCode,
                initiatingAction:
                    prev.phoneCallId === attrs.phoneCallId ? prev.initiatingAction : null,
            }));

            void invalidatePhoneCalls(attrs.dossierId);
        },
        [stopTranscriptGeneration, stopSummaryGeneration, invalidatePhoneCalls],
    );

    const handleTranscriptEvent = useCallback(
        (event: PhoneCallTranscriptWebSocketEvent) => {
            const { attributes, id: dossierPhoneCallId } = event.data;

            if (!attributes.action) return;

            const { process, eventType, isSummaryOnly } = mapTranscriptEvent(
                stateRef.current,
                attributes,
            );

            if (process === 'started' && eventType) {
                handleProcessStarted(attributes, dossierPhoneCallId, { isSummaryOnly, eventType });
            } else if (process === 'failed') {
                handleProcessFailed(attributes, dossierPhoneCallId);
            } else if (process === 'completed' && eventType) {
                handleProcessCompleted(attributes, dossierPhoneCallId, {
                    isSummaryOnly,
                    eventType,
                });
            }
        },
        [handleProcessStarted, handleProcessFailed, handleProcessCompleted],
    );

    useEffect(() => {
        if (!socket) return;

        const handleMessage = (event: MessageEvent) => {
            if (!event.data) return;

            try {
                const response = JSON.parse(event.data) as PhoneCallTranscriptWebSocketEvent;
                const messageType = response.data?.type;

                // Check if this is a dossier-phone-call event
                if (messageType === 'dossier-phone-call') {
                    // Filter events by agentId to avoid dispatching to the entire agency
                    const eventAgentId = response.data.attributes.agentId;

                    // Only handle the event if the agentId matches the current user
                    if (eventAgentId.toString() === currentUserId?.toString()) {
                        handleTranscriptEvent(response);
                    }
                }
            } catch (error) {
                if (event.data.includes('dossier-phone-call')) {
                    console.error('Error parsing transcript WebSocket message:', error);
                }
            }
        };

        socket.addEventListener('message', handleMessage);

        return () => {
            socket.removeEventListener('message', handleMessage);
        };
    }, [handleTranscriptEvent, socket, currentUserId]);

    const dismissNotification = () => {
        setNotificationState((curr) => ({ ...curr, isVisible: false }));
    };

    const getAlertSeverity = () => {
        switch (notificationState.type) {
            case 'call-transcription-started':
            case 'call-summary-started':
                return 'info' as const;
            case 'call-transcription-ended':
            case 'call-summary-ended':
                return 'success' as const;
            case 'call-transcription-failed':
                return 'error' as const;
            default:
                return 'info' as const;
        }
    };

    const renderAlertContent = () => {
        if (!notificationState.dossierId || !notificationState.phoneCallId) {
            return null;
        }

        const isSummaryOnly =
            notificationState.initiatingAction ===
            PHONE_CALL_TRANSCRIPT_WEBSOCKET_ACTIONS.SUMMARY_REGENERATION_REQUESTED;

        switch (notificationState.type) {
            case 'call-transcription-started':
            case 'call-summary-started':
                return <ProcessingStartedNotification isSummaryOnly={isSummaryOnly} />;

            case 'call-transcription-ended':
            case 'call-summary-ended':
                return (
                    <ProcessingCompletedNotification
                        isSummaryOnly={isSummaryOnly}
                        dossierId={notificationState.dossierId}
                        phoneCallId={notificationState.phoneCallId}
                        onDismiss={dismissNotification}
                    />
                );

            case 'call-transcription-failed':
                return (
                    <ProcessingFailedNotification
                        transcriptErrorCode={notificationState.transcriptErrorCode}
                        summaryErrorCode={notificationState.summaryErrorCode}
                        dossierId={notificationState.dossierId}
                        dossierPhoneCallId={notificationState.dossierPhoneCallId}
                        dismissNotification={dismissNotification}
                    />
                );

            default:
                return null;
        }
    };

    const getSnackbarWidth = () => {
        switch (notificationState.type) {
            case 'call-transcription-started':
                return { minWidth: { xs: '90vw', sm: 472 } };
            case 'call-summary-started':
                return { minWidth: { xs: '90vw', sm: 380 } };
            case 'call-transcription-ended':
            case 'call-summary-ended':
                return { minWidth: { xs: '90vw', sm: 354 } };
            case 'call-transcription-failed':
                return { minWidth: { xs: '90vw', sm: 300 } };
            default:
                return { minWidth: { xs: '90vw', sm: 300 } };
        }
    };

    if (!notificationState.isVisible || !notificationState.type) {
        return null;
    }

    const isNotificationStartType =
        notificationState.type === 'call-transcription-started' ||
        notificationState.type === 'call-summary-started';

    return (
        <Snackbar
            open={notificationState.isVisible}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            sx={{
                ...getSnackbarWidth(),
            }}
        >
            <Alert
                severity={getAlertSeverity()}
                iconMapping={{
                    info: <SyncIcon />,
                    success: <CheckIcon fontSize="small" />,
                }}
                onClose={dismissNotification}
                sx={{
                    width: '100%',
                    alignItems: isNotificationStartType ? 'center' : 'flex-start',
                    '& .MuiAlert-action': {
                        marginTop: isNotificationStartType ? '-2px' : '-8px',
                    },
                }}
            >
                {renderAlertContent()}
            </Alert>
        </Snackbar>
    );
};
